import { useState } from 'react'
import type { FC } from 'react'

import { Button, Skeleton } from 'antd'
import { useLocation, useNavigate, useParams } from 'react-router'

import { Warning } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/Warning/Warning'
import { PathUtils } from 'routes/routes.utils'

import { useFetchSeriesTemplatesQuery } from '../../api/series.api'
import type { SeriesTemplate } from '../../api/series.types'
import { TemplateModal } from '../../templates/TemplateModal/TemplateModal'
import { StatsBar, type StatItem } from '../shared/StatsBar'
import { SERIES_PAGES_TEXT } from '../shared/seriesPagesText'
import shared from '../shared/seriesPage.module.scss'
import { SeriesTemplatesTable } from './SeriesTemplatesTable'
import styles from './seriesTemplatesPage.module.scss'
import { useTemplateActions, type TemplateBusy } from './useTemplateActions'

const TEXT = SERIES_PAGES_TEXT.templates
const SKELETON_ROWS = 4

const toStats = (templates: SeriesTemplate[] | undefined): StatItem[] => {
  const available = templates?.filter((template) => template.active).length ?? 0
  return [
    { label: TEXT.stats.templates, value: templates ? templates.length : null, tone: 'default' },
    { label: TEXT.stats.available, value: templates ? available : null, tone: 'active' },
    {
      label: TEXT.stats.retired,
      value: templates ? templates.length - available : null,
      tone: 'muted',
    },
  ]
}

type ResultsProps = {
  templates: SeriesTemplate[] | undefined
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  busy: TemplateBusy | null
  onEdit: (template: SeriesTemplate) => void
  onDuplicate: (template: SeriesTemplate) => void
  onToggleActive: (template: SeriesTemplate) => void
}

const Results: FC<ResultsProps> = ({ templates, isLoading, isError, onRetry, ...handlers }) => {
  if (isError) {
    return (
      <div className={styles.card}>
        <div className={styles.message} role='alert'>
          {TEXT.error}
          <Button className={styles.button} onClick={onRetry}>
            {TEXT.tryAgain}
          </Button>
        </div>
      </div>
    )
  }
  if (isLoading || !templates) {
    return (
      <div className={styles.card} aria-busy='true'>
        <Skeleton
          active
          title={false}
          paragraph={{ rows: SKELETON_ROWS }}
          className={styles.skeleton}
        />
      </div>
    )
  }
  if (templates.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.message}>{TEXT.empty}</div>
      </div>
    )
  }
  return (
    <div className={styles.card}>
      <SeriesTemplatesTable rows={templates} {...handlers} />
    </div>
  )
}

// Only a query string handed over by the list page is trusted.
const toListSearch = (state: unknown): string => {
  const value = (state as { listSearch?: unknown } | null)?.listSearch
  return typeof value === 'string' && value.startsWith('?') ? value : ''
}

export const SeriesTemplatesPage: FC = () => {
  const { siteId = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const listSearch = toListSearch(location.state)
  const { data, isLoading, isError, refetch } = useFetchSeriesTemplatesQuery(
    { siteId, includeRetired: true },
    { refetchOnMountOrArgChange: true },
  )
  const actions = useTemplateActions(siteId)
  const [editing, setEditing] = useState<SeriesTemplate | 'new' | null>(null)

  return (
    <section className={shared.page}>
      <header className={styles.header}>
        <h1 className={shared.title}>{TEXT.title}</h1>
        <span className={styles.spacer} />
        <Button
          className={styles.button}
          onClick={() => navigate(`${PathUtils.getScheduleSeries(siteId)}${listSearch}`)}>
          {TEXT.back}
        </Button>
      </header>
      <StatsBar items={toStats(data)} />
      <div className={styles.infoCard}>
        <Button className={styles.addButton} onClick={() => setEditing('new')}>
          {TEXT.add}
        </Button>
        <span className={styles.info}>{TEXT.info}</span>
      </div>
      <Results
        templates={data}
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        busy={actions.busy}
        onEdit={setEditing}
        onDuplicate={actions.duplicate}
        onToggleActive={actions.toggleActive}
      />
      <Warning {...actions.warningProps} />
      <TemplateModal
        open={editing !== null}
        template={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
    </section>
  )
}
